import { Lock } from 'lucide-react';
import { useTranslation } from '@doccareplus/shared/lib';
import { Button, Card } from '@doccareplus/ui';
import { SUPPORT_EMAIL } from '@/config';

export interface SubscriptionPendingPaymentScreenProps {
  onCheckAgain: () => void;
  isChecking?: boolean;
  /** The plan name/price the clinic is waiting to have confirmed, when known. */
  planName?: string | null;
  currency?: string | null;
  finalPrice?: number | null;
}

function money(currency: string | null | undefined, amount: number | null | undefined) {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch {
    return `${currency ?? ''} ${amount}`.trim();
  }
}

/**
 * Full-workspace lock shown in place of the normal dashboard when a subscription is PendingPayment —
 * no trial, no payment gateway, so nothing is owed-and-confirmed yet. Backend already blocks writes for
 * this status; this replaces the entire routed content so the clinic can't browse around it.
 */
export function SubscriptionPendingPaymentScreen({
  onCheckAgain,
  isChecking,
  planName,
  currency,
  finalPrice,
}: SubscriptionPendingPaymentScreenProps) {
  const { t } = useTranslation('common');
  const formattedPrice = money(currency, finalPrice);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card>
        <div className="flex max-w-[420px] flex-col items-center gap-3 px-6 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--warning-bg)] text-[var(--warning)]">
            <Lock className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="text-[16px] font-semibold text-[var(--text)]">
            {t('subscription.pendingPayment.title')}
          </div>
          {planName && (
            <div className="rounded-[10px] bg-[var(--surface-2)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text)]">
              {formattedPrice
                ? t('subscription.pendingPayment.planWithPrice', { plan: planName, price: formattedPrice })
                : planName}
            </div>
          )}
          <div className="text-[13.5px] text-[var(--text-2)]">
            {t('subscription.pendingPayment.body')}
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-[13.5px] font-semibold text-[var(--primary)] underline"
          >
            {SUPPORT_EMAIL}
          </a>
          <Button variant="ghost" size="sm" onClick={onCheckAgain} disabled={isChecking}>
            {t('subscription.pendingPayment.checkAgain')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
